import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export interface EnergyDataPoint {
  year: number;
  total_kwh?: number;
  total_therms?: number;
  method_note?: string;
  data_source?: string;
}

export interface ExtrapolationResult {
  years: number[];
  values: (number | null)[];
  sources: ('observed' | 'interpolated' | 'extrapolated')[];
  providers: string[]; // Data sources (utility names) for each year
  dataSources: (string | null)[]; // data_source column value from database for observed data
}

/**
 * Interpolate missing values between known data points
 */
function interpolateValues(years: number[], values: (number | null)[]): (number | null)[] {
  const interpolated = [...values];
  
  for (let i = 0; i < years.length; i++) {
    if (interpolated[i] === null || interpolated[i] === undefined) {
      // Find previous and next non-null values
      let prevIndex = -1;
      let nextIndex = -1;
      
      for (let j = i - 1; j >= 0; j--) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          prevIndex = j;
          break;
        }
      }
      
      for (let j = i + 1; j < years.length; j++) {
        if (interpolated[j] !== null && interpolated[j] !== undefined) {
          nextIndex = j;
          break;
        }
      }
      
      if (prevIndex !== -1 && nextIndex !== -1) {
        const prevVal = interpolated[prevIndex]!;
        const nextVal = interpolated[nextIndex]!;
        const prevYear = years[prevIndex];
        const nextYear = years[nextIndex];
        const currentYear = years[i];
        
        interpolated[i] = prevVal + (nextVal - prevVal) * (currentYear - prevYear) / (nextYear - prevYear);
      }
    }
  }
  
  return interpolated;
}

/**
 * Extrapolate missing values using trend-based approach for both forward and backward projection
 * Based on the carbon intensity methodology
 */
function extrapolateValues(years: number[], inputValues: (number | null)[], dataType: 'electricity' | 'natural_gas'): (number | null)[] {
  const extrapolated = [...inputValues];
  
  // Find all non-null values and their indices
  const validDataPoints: Array<{index: number, year: number, value: number}> = [];
  for (let i = 0; i < years.length; i++) {
    if (inputValues[i] !== null && inputValues[i] !== undefined) {
      validDataPoints.push({
        index: i,
        year: years[i],
        value: inputValues[i] as number
      });
    }
  }
  
  if (validDataPoints.length === 0) {
    return extrapolated; // No data to work with
  }
  
  // Sort by year to ensure proper order
  validDataPoints.sort((a, b) => a.year - b.year);
  
  const firstPoint = validDataPoints[0];
  const lastPoint = validDataPoints[validDataPoints.length - 1];
  
  // Detect outliers using IQR method
  function isOutlier(value: number, allValues: number[]): boolean {
    if (allValues.length < 4) return false; // Need at least 4 points to detect outliers
    const sorted = [...allValues].sort((a, b) => a - b);
    const q1Index = Math.floor(sorted.length * 0.25);
    const q3Index = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    if (iqr === 0) return false; // All values are the same
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    return value < lowerBound || value > upperBound;
  }
  
  // Remove outliers for trend calculation
  const dataPointValues = validDataPoints.map(p => p.value);
  const cleanDataPoints = validDataPoints.filter(p => !isOutlier(p.value, dataPointValues));
  
  // Use clean points for trend, fallback to all points if too few remain
  const trendDataPoints = cleanDataPoints.length >= 2 ? cleanDataPoints : validDataPoints;
  
  // Get the last clean point for forward extrapolation (to avoid using outliers as baseline)
  // If all points are clean, use lastPoint; otherwise use the last clean point
  const lastCleanPoint = cleanDataPoints.length > 0 
    ? cleanDataPoints[cleanDataPoints.length - 1]
    : lastPoint;
  
  // Calculate overall trend from clean data points
  // Use recent points (last 5 years) if available for more accurate trend
  let overallTrend = 0;
  if (trendDataPoints.length >= 2) {
    const recentPoints = trendDataPoints.slice(-5); // Use last 5 points if available
    const trendSource = recentPoints.length >= 2 ? recentPoints : trendDataPoints;
    const trendFirst = trendSource[0];
    const trendLast = trendSource[trendSource.length - 1];
    const yearDiff = trendLast.year - trendFirst.year;
    const valueDiff = trendLast.value - trendFirst.value;
    overallTrend = yearDiff > 0 ? valueDiff / yearDiff : 0;
  }
  
  // Calculate representative maximum (use 75th percentile instead of absolute max to avoid outlier influence)
  const sortedValues = trendDataPoints.map(p => p.value).sort((a, b) => a - b);
  const p75Index = Math.floor(sortedValues.length * 0.75);
  const representativeMax = sortedValues.length > 0 
    ? (sortedValues[p75Index] || sortedValues[sortedValues.length - 1])
    : 0;
  
  // Apply trend-based projection to all missing values
  for (let i = 0; i < years.length; i++) {
    if (extrapolated[i] === null || extrapolated[i] === undefined) {
      // For forward extrapolation, use last CLEAN point as baseline (not outlier)
      // For backward extrapolation, use first CLEAN point as baseline (not outlier)
      const targetYear = years[i];
      const firstCleanPoint = cleanDataPoints.length > 0 
        ? cleanDataPoints[0] 
        : firstPoint;
      const baselinePoint = targetYear > lastCleanPoint.year ? lastCleanPoint : firstCleanPoint;
      const yearDiff = years[i] - baselinePoint.year;
      const projectedValue = baselinePoint.value + (overallTrend * yearDiff);
      
      // Apply reasonable bounds based on data type
      let minValue = 0; // Energy consumption can't be negative
      
      // Use representative maximum (75th percentile) to avoid outlier influence
      let maxValue = representativeMax * 1.2; // Cap at 1.2x representative max
      
      // Additional conservative bounds for early years (backcasting)
      if (years[i] < 2010 && projectedValue > maxValue * 0.8) {
        maxValue = maxValue * 0.8; // Cap early years at 80% of max observed
      }
      
      // For forward extrapolation, be more conservative
      // Don't extrapolate far into the future if data is sparse or inconsistent
      if (years[i] > lastCleanPoint.year) {
        const yearsForward = years[i] - lastCleanPoint.year;
        // If extrapolating more than 2 years forward, cap at last CLEAN value
        if (yearsForward > 2) {
          maxValue = Math.max(maxValue, lastCleanPoint.value * 1.1); // Only allow 10% growth max
        }
      }
      
      // For electricity, apply additional bounds based on historical patterns
      if (dataType === 'electricity') {
        // Electricity consumption tends to be more stable, use tighter bounds
        maxValue = representativeMax * 1.1;
        if (years[i] < 2010) {
          maxValue = maxValue * 0.9;
        }
      }
      
      // For natural gas, allow for more variation due to weather and economic factors
      if (dataType === 'natural_gas') {
        maxValue = representativeMax * 1.2; // Use representative max, not absolute max
        if (years[i] < 2010) {
          maxValue = maxValue * 0.85;
        }
        // For forward extrapolation of natural gas, be conservative
        if (years[i] > lastCleanPoint.year) {
          const yearsForward = years[i] - lastCleanPoint.year;
          // Cap forward extrapolation to prevent unrealistic jumps
          if (yearsForward > 1) {
            // Don't allow more than 20% increase per year for natural gas (based on last CLEAN point)
            maxValue = Math.min(maxValue, lastCleanPoint.value * Math.pow(1.2, yearsForward));
          }
        }
      }
      
      const finalValue = Math.max(minValue, Math.min(maxValue, projectedValue));
      extrapolated[i] = finalValue;
    }
  }
  
  // Debug logging removed - extrapolation working correctly
  
  return extrapolated;
}

/**
 * Process energy data with trend-based extrapolation methodology
 */
export async function processEnergyDataWithExtrapolation(
  location: string,
  table: 'db_elec_residential_clean' | 'db_elec_residential' | 'db_elec_commercial' | 'db_ng_residential' | 'db_ng_commercial',
  dataType: 'electricity' | 'natural_gas',
  years: number[] = Array.from({ length: 20 }, (_, i) => 2005 + i)
): Promise<ExtrapolationResult> {
  
  // Determine which columns to select based on table type
  const isElectricityTable = table.includes('elec');
  const valueColumn = isElectricityTable ? 'total_kwh' : 'total_therms';
  const selectColumns = `year, ${valueColumn}, method_note, data_source, measurement, location_name, geofips`;
  
  // Fetch raw data from Supabase
  let rawData: any[] | null = null;
  let error: any = null;

  // For tables that should be queried by geofips: residential electricity clean and natural gas tables
  const tablesByGeofips = ['db_elec_residential_clean', 'db_ng_residential', 'db_ng_commercial'];
  
  if (tablesByGeofips.includes(table)) {
    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`No geofips found for location "${location}" while querying ${table}`);
    }

    const res = await supabaseClient
      .from(table)
      .select(selectColumns)
      .eq('geofips', geofips)
      .order('year', { ascending: true });
    rawData = (res.data || []) as any[];
    error = res.error;
  } else {
    const res = await supabaseClient
      .from(table)
      .select(selectColumns)
      .eq('location_name', location)
      .order('year', { ascending: true });
    rawData = (res.data || []) as any[];
    error = res.error;
  }
  
  if (error) {
    throw new Error(`Error fetching ${dataType} data: ${error.message}`);
  }
  
  const rows = (rawData || []) as any[];
  
  // Enhanced debug logging to understand duplicate detection
  if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('berkeley')) {
    const uniqueLocationNames = Array.from(new Set(rows.map(r => r.location_name || 'MISSING')));
    const allValues = rows.map(r => Number(isElectricityTable ? r.total_kwh : r.total_therms) || 0);
    const yearCounts = rows.reduce((acc: Record<number, number>, r) => {
      acc[r.year] = (acc[r.year] || 0) + 1;
      return acc;
    }, {});
    
    const extrapolatedRows = rows.filter(r => {
      const measurement = String(r.measurement || '').toLowerCase();
      const methodNote = String(r.method_note || '').toLowerCase();
      return measurement.includes('extrapolated') || methodNote.includes('extrapol');
    });
    
    // Debug logging removed - data fetching working correctly
  }
  
  // Check for duplicate years and warn
  const yearsInData = rows.map(r => r.year);
  const duplicateYears = yearsInData.filter((year, idx) => yearsInData.indexOf(year) !== idx);
  if (duplicateYears.length > 0) {
    const uniqueDuplicates = Array.from(new Set(duplicateYears));
    console.warn(`⚠️ WARNING: Found duplicate years in ${table} for "${location}":`, uniqueDuplicates);
    uniqueDuplicates.forEach(year => {
      const yearRows = rows.filter(r => r.year === year);
      console.warn(`  Year ${year} has ${yearRows.length} rows:`, yearRows.map(r => ({
        location_name: r.location_name,
        value: isElectricityTable ? r.total_kwh : r.total_therms,
        method_note: r.method_note,
        data_source: r.data_source
      })));
    });
  }
  
  // Create arrays for each year
  // Special rule for tables queried by geofips (db_elec_residential_clean, db_ng_residential, db_ng_commercial):
  //  - Query is by geofips only; do NOT sum duplicates. Prefer a single observed row when multiples exist.
  // General rule (other tables):
  //  - If multiple rows exist for the same year and all are observed from different utilities, SUM them; otherwise prefer observed.
  // IMPORTANT: Exclude previously extrapolated values from database - only use observed/interpolated as baseline
  const values: (number | null)[] = years.map(year => {
    const yearRows = rows.filter(r => {
      if (r.year !== year) return false;
      // Filter out rows that are marked as "extrapolated" in the database
      // We only want observed or interpolated data as baseline for new extrapolation
      const measurement = String(r.measurement || '').toLowerCase();
      const methodNote = String(r.method_note || '').toLowerCase();
      const isExtrapolated = measurement.includes('extrapolated') || methodNote.includes('extrapol');
      return !isExtrapolated; // Exclude extrapolated values
    });
    if (yearRows.length === 0) return null;
    
    // Check if all rows are "observed" (no method_note or method_note doesn't include 'extrapol' or 'interpol')
    const allObserved = yearRows.every(r => {
      const note = String(r.method_note || '').toLowerCase();
      return !note.includes('extrapol') && !note.includes('interpol');
    });
    
    // Check if rows have different data_source values (indicating different utilities)
    const uniqueDataSources = Array.from(new Set(yearRows.map(r => r.data_source).filter(Boolean)));
    const hasMultipleUtilities = uniqueDataSources.length > 1;
    
    let finalValue: number | null = null;
    
    if (!tablesByGeofips.includes(table) && yearRows.length > 1 && allObserved && hasMultipleUtilities) {
      // If multiple rows, all observed, AND different utilities (data_sources), SUM them
      const sum = yearRows.reduce((acc, r) => {
        const value = isElectricityTable ? r.total_kwh : r.total_therms;
        return acc + (Number(value) || 0);
      }, 0);
      finalValue = sum > 0 ? sum : null;
      
      if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('berkeley')) {
        console.log(`📊 Multiple observed rows with different utilities for ${location} year ${year}, summing values:`, {
          rowCount: yearRows.length,
          uniqueUtilities: uniqueDataSources,
          values: yearRows.map(r => ({
            data_source: r.data_source,
            value: isElectricityTable ? r.total_kwh : r.total_therms
          })),
          sum: finalValue
        });
      }
    } else if (yearRows.length > 1) {
      // Multiple rows but either not all observed, or same utility - prefer observed over interpolated/extrapolated
      if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('berkeley')) {
        console.log(`⚠️ Multiple rows for ${location} year ${year}, selecting one (not summing):`, {
          rowCount: yearRows.length,
          allObserved,
          uniqueUtilities: uniqueDataSources,
          hasMultipleUtilities,
          rows: yearRows.map(r => ({
            data_source: r.data_source,
            method_note: r.method_note,
            value: isElectricityTable ? r.total_kwh : r.total_therms
          }))
        });
      }
      // If multiple rows and not all observed, prefer observed > interpolated > extrapolated
      yearRows.sort((a, b) => {
        const aNote = String(a.method_note || '').toLowerCase();
        const bNote = String(b.method_note || '').toLowerCase();
        const aPriority = aNote.includes('extrapol') ? 3 : (aNote.includes('interpol') ? 2 : 1);
        const bPriority = bNote.includes('extrapol') ? 3 : (bNote.includes('interpol') ? 2 : 1);
        return aPriority - bPriority;
      });
      const selectedRow = yearRows[0];
      const value = isElectricityTable ? selectedRow.total_kwh : selectedRow.total_therms;
      finalValue = value ? Number(value) : null;
      
      console.warn(`⚠️ Multiple rows for ${location} year ${year}, selected:`, {
        value: finalValue,
        method_note: selectedRow.method_note,
        data_source: selectedRow.data_source,
        allRows: yearRows.map(r => ({
          data_source: r.data_source,
          method_note: r.method_note,
          value: isElectricityTable ? r.total_kwh : r.total_therms
        }))
      });
    } else {
      // Single row
      const value = isElectricityTable ? yearRows[0].total_kwh : yearRows[0].total_therms;
      finalValue = value ? Number(value) : null;
    }
    
    return finalValue;
  });
  
  // Determine provenance from 'measurement' column when available
  const sources: ('observed' | 'interpolated' | 'extrapolated')[] = years.map(year => {
    const yearRows = rows.filter(r => r.year === year);
    if (yearRows.length === 0) return 'extrapolated';

    // Prefer measurement column if present
    const normalize = (s: any) => String(s || '').toLowerCase();
    const measurementValues = yearRows.map(r => normalize(r.measurement)).filter(Boolean);
    const hasMeasurement = measurementValues.length > 0;

    if (hasMeasurement) {
      // If multiple measurements, choose priority observed > interpolated > extrapolated
      const priority = ['observed', 'interpolated', 'extrapolated'];
      const found = priority.find(p => measurementValues.some(m => m.includes(p)));
      if (found === 'observed') return 'observed';
      if (found === 'interpolated') return 'interpolated';
      if (found === 'extrapolated') return 'extrapolated';
    }

    // Fall back to method_note if measurement wasn't provided
    const allObserved = yearRows.every(r => {
      const note = String(r.method_note || '').toLowerCase();
      return !note.includes('extrapol') && !note.includes('interpol');
    });
    const uniqueDataSources = Array.from(new Set(yearRows.map(r => r.data_source).filter(Boolean)));
    const hasMultipleUtilities = uniqueDataSources.length > 1;
    // For tables queried by geofips, prefer single row selection; otherwise sum if multiple utilities
    if (yearRows.length > 1 && allObserved && hasMultipleUtilities && !tablesByGeofips.includes(table)) return 'observed';

    let selectedRow = yearRows[0];
    if (yearRows.length > 1) {
      yearRows.sort((a, b) => {
        const aNote = String(a.method_note || '').toLowerCase();
        const bNote = String(b.method_note || '').toLowerCase();
        const aPriority = aNote.includes('extrapol') ? 3 : (aNote.includes('interpol') ? 2 : 1);
        const bPriority = bNote.includes('extrapol') ? 3 : (bNote.includes('interpol') ? 2 : 1);
        return aPriority - bPriority;
      });
      selectedRow = yearRows[0];
    }
    const note = String(selectedRow.method_note ?? '').toLowerCase().trim();
    if (!note) return 'observed';
    if (note.includes('interpol')) return 'interpolated';
    if (note.includes('extrapol')) return 'extrapolated';
    return 'observed';
  });
  
  const providers: string[] = years.map(year => {
    const yearRows = rows.filter(r => r.year === year);
    if (yearRows.length === 0) return 'Trend-based extrapolation';
    
    // Check if all rows are "observed"
    const allObserved = yearRows.every(r => {
      const note = String(r.method_note || '').toLowerCase();
      return !note.includes('extrapol') && !note.includes('interpol');
    });
    
    // Check if rows have different data_source values (indicating different utilities)
    const uniqueDataSources = Array.from(new Set(yearRows.map(r => r.data_source).filter(Boolean)));
    const hasMultipleUtilities = uniqueDataSources.length > 1;
    
    // If multiple rows and all observed AND different utilities, combine data sources
    // For tables queried by geofips, prefer single row selection; otherwise combine if multiple utilities
    if (yearRows.length > 1 && allObserved && hasMultipleUtilities && !tablesByGeofips.includes(table)) {
      return uniqueDataSources.length > 0 ? uniqueDataSources.join(', ') : 'Multiple sources';
    }
    
    // Otherwise, use same selection logic as values array
    let selectedRow = yearRows[0];
    if (yearRows.length > 1) {
      yearRows.sort((a, b) => {
        const aNote = String(a.method_note || '').toLowerCase();
        const bNote = String(b.method_note || '').toLowerCase();
        const aPriority = aNote.includes('extrapol') ? 3 : (aNote.includes('interpol') ? 2 : 1);
        const bPriority = bNote.includes('extrapol') ? 3 : (bNote.includes('interpol') ? 2 : 1);
        return aPriority - bPriority;
      });
      selectedRow = yearRows[0];
    }
    
    return selectedRow?.data_source || 'Trend-based extrapolation';
  });
  
  // Debug: Log values before interpolation/extrapolation for San Luis Obispo
  if (location.toLowerCase().includes('san luis obispo')) {
    console.log(`🔍 DEBUG: Values array BEFORE interpolation/extrapolation for ${location}:`, {
      years: years,
      values: values.map((v, i) => ({ year: years[i], value: v })),
      nullCount: values.filter(v => v === null).length,
      nonNullValues: values.filter(v => v !== null).map((v, i) => ({ year: years[values.indexOf(v)], value: v }))
    });
  }
  
  // Apply interpolation first (only fills null gaps; preserves measured values)
  const interpolatedValues = interpolateValues(years, values);
  
  // Debug: Log after interpolation
  if (location.toLowerCase().includes('san luis obispo')) {
    console.log(`🔍 DEBUG: Values AFTER interpolation for ${location}:`, {
      years: years,
      values: interpolatedValues.map((v, i) => ({ year: years[i], value: v })),
      changed: interpolatedValues.map((v, i) => ({ 
        year: years[i], 
        before: values[i], 
        after: v, 
        changed: values[i] !== v 
      })).filter(x => x.changed)
    });
  }
  
  // Then apply extrapolation
  const finalValues = extrapolateValues(years, interpolatedValues, dataType);
  
  // Debug: Log after extrapolation
  if (location.toLowerCase().includes('san luis obispo')) {
    console.log(`🔍 DEBUG: Final values AFTER extrapolation for ${location}:`, {
      years: years,
      values: finalValues.map((v, i) => ({ year: years[i], value: v })),
      changed: finalValues.map((v, i) => ({ 
        year: years[i], 
        before: interpolatedValues[i], 
        after: v, 
        changed: interpolatedValues[i] !== v 
      })).filter(x => x.changed)
    });
  }
  
  // Update sources array to reflect interpolation/extrapolation while preserving provided measurement
  const finalSources: ('observed' | 'interpolated' | 'extrapolated')[] = years.map((year, index) => {
    if (sources[index] === 'observed' || sources[index] === 'interpolated' || sources[index] === 'extrapolated') {
      return sources[index];
    }
    const originalValue = values[index];
    const interpolatedValue = interpolatedValues[index];
    if (originalValue !== null && originalValue !== undefined) return 'observed';
    if (interpolatedValue !== null && interpolatedValue !== undefined) return 'interpolated';
    return 'extrapolated';
  });
  
  // Create dataSources array - data_source column value for observed data, null otherwise
  const dataSources: (string | null)[] = years.map((year, index) => {
    if (finalSources[index] !== 'observed') return null;
    
    const yearRows = rows.filter(r => r.year === year);
    if (yearRows.length === 0) return null;
    
    // Check if all rows are observed and have different data_source values
    const allObserved = yearRows.every(r => {
      const note = String(r.method_note || '').toLowerCase();
      return !note.includes('extrapol') && !note.includes('interpol');
    });
    const uniqueDataSources = Array.from(new Set(yearRows.map(r => r.data_source).filter(Boolean)));
    const hasMultipleUtilities = uniqueDataSources.length > 1;
    
    // If multiple observed rows with different utilities, return combined string
    if (yearRows.length > 1 && allObserved && hasMultipleUtilities && !tablesByGeofips.includes(table)) {
      return uniqueDataSources.join(', ');
    }
    
    // Otherwise, return the data_source from the selected row
    let selectedRow = yearRows[0];
    if (yearRows.length > 1) {
      yearRows.sort((a, b) => {
        const aNote = String(a.method_note || '').toLowerCase();
        const bNote = String(b.method_note || '').toLowerCase();
        const aPriority = aNote.includes('extrapol') ? 3 : (aNote.includes('interpol') ? 2 : 1);
        const bPriority = bNote.includes('extrapol') ? 3 : (bNote.includes('interpol') ? 2 : 1);
        return aPriority - bPriority;
      });
      selectedRow = yearRows[0];
    }
    
    return selectedRow?.data_source || null;
  });
  
  return {
    years,
    values: finalValues,
    sources: finalSources,
    providers,
    dataSources
  };
}

/**
 * Update Supabase data with trend-based extrapolated values
 */
export async function updateSupabaseWithTrendExtrapolation(
  location: string,
  table: 'db_elec_residential_clean' | 'db_elec_residential' | 'db_elec_commercial' | 'db_ng_residential' | 'db_ng_commercial',
  dataType: 'electricity' | 'natural_gas'
): Promise<void> {
  
  console.log(`🔄 Updating ${dataType} data for ${location} in ${table} with trend-based extrapolation`);
  
  // Process data with trend-based extrapolation
  const result = await processEnergyDataWithExtrapolation(location, table, dataType);
  
  // Identify which values need to be updated (extrapolated values)
  const updates: Array<{
    year: number;
    value: number;
    method_note: string;
    data_source: string;
  }> = [];
  
  result.years.forEach((year, index) => {
    if (result.sources[index] === 'extrapolated' && result.values[index] !== null) {
      updates.push({
        year,
        value: result.values[index]!,
        method_note: 'Trend-based extrapolation using overall data trend and conservative bounds',
        data_source: 'ClimatePlans.org trend analysis'
      });
    }
  });
  
  if (updates.length === 0) {
    console.log(`✅ No extrapolated values to update for ${location} in ${table}`);
    return;
  }
  
  console.log(`📊 Updating ${updates.length} extrapolated values for ${location} in ${table}`);
  
  // Update each extrapolated value in Supabase
  for (const update of updates) {
    const isElectricityTable = table.includes('elec');
    const valueField = isElectricityTable ? 'total_kwh' : 'total_therms';
    
    const { error } = await supabaseClient
      .from(table)
      .upsert({
        location_name: location,
        year: update.year,
        [valueField]: update.value,
        method_note: update.method_note,
        data_source: update.data_source
      });
    
    if (error) {
      console.error(`❌ Error updating ${table} for ${location} ${update.year}:`, error);
    } else {
      console.log(`✅ Updated ${table} for ${location} ${update.year}: ${update.value}`);
    }
  }
  
  console.log(`🎉 Completed trend-based extrapolation update for ${location} in ${table}`);
}

/**
 * Batch update multiple locations and tables with trend-based extrapolation
 */
export async function batchUpdateTrendExtrapolation(
  locations: string[],
  tables: Array<'db_elec_residential_clean' | 'db_elec_residential' | 'db_elec_commercial' | 'db_ng_residential' | 'db_ng_commercial'>,
  dataTypes: Array<'electricity' | 'natural_gas'>
): Promise<void> {
  
  console.log(`🚀 Starting batch trend-based extrapolation update for ${locations.length} locations`);
  
  for (const location of locations) {
    console.log(`📍 Processing location: ${location}`);
    
    for (let i = 0; i < tables.length; i++) {
      const table = tables[i];
      const dataType = dataTypes[i];
      
      try {
        await updateSupabaseWithTrendExtrapolation(location, table, dataType);
        // Add small delay to avoid overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`❌ Error processing ${location} ${table}:`, error);
      }
    }
  }
  
  console.log(`🎉 Completed batch trend-based extrapolation update`);
}
